import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { OrdersPageHeader } from "./orders-page-header";
import { OrdersStats } from "./orders-stats";
import { OrdersFilterBar } from "./orders-filter-bar";
import { OrdersTable } from "./orders-table";
import { OrdersPagination } from "./orders-pagination";
import {
  computeStatCounts,
  countOrdersByTab,
  filterOrders,
  paginateSlice,
  totalPagesFor,
} from "./orders-utils";
import { mapSellerOrderItemToRow } from "./orders-mapper";
import { GET_ORDER_STATS, GET_SELLER_ORDERS_LIST } from "../../../../Utils/Queries";

const PAGE_SIZE = 10;

export default function SellerOrdersPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);

  const { data: statsData } = useQuery(GET_ORDER_STATS, {
    fetchPolicy: "cache-and-network",
  });

  const {
    data: ordersData,
    loading: ordersLoading,
    error: ordersError,
  } = useQuery(GET_SELLER_ORDERS_LIST, {
    variables: { page: null, limit: null },
    fetchPolicy: "network-only",
  });

  const mappedRows = useMemo(() => {
    const raw = ordersData?.getSellerOrders?.items;
    if (!Array.isArray(raw)) return [];
    return raw.map(mapSellerOrderItemToRow);
  }, [ordersData]);

  const statCounts = useMemo(() => {
    const s = statsData?.getOrderStats;
    if (s) {
      return {
        new: Number(s.newOrders) || 0,
        processing: Number(s.processing) || 0,
        out_delivery: Number(s.outForDelivery) || 0,
        delivered: Number(s.delivered) || 0,
      };
    }
    return computeStatCounts(mappedRows);
  }, [statsData, mappedRows]);

  const tabCounts = useMemo(() => countOrdersByTab(mappedRows), [mappedRows]);

  const filtered = useMemo(
    () =>
      filterOrders(mappedRows, {
        tab: activeTab,
        search,
        category,
      }),
    [mappedRows, activeTab, search, category]
  );

  const totalPages = totalPagesFor(filtered.length, PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [activeTab, search, category]);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => paginateSlice(filtered, safePage, PAGE_SIZE),
    [filtered, safePage]
  );

  const handleView = (order) => {
    navigate(`/seller/orders/${order.id}`);
  };

  return (
    <div className="pb-12 pt-10 md:pt-6">
      <OrdersPageHeader onExport={() => {}} />

      <OrdersStats counts={statCounts} />

      <div className="min-w-0 rounded-[14px] border border-[#EFEFEF] bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)] sm:p-5 md:p-6 lg:p-8">
        <OrdersFilterBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabCounts={tabCounts}
          search={search}
          onSearchChange={setSearch}
          category={category}
          onCategoryChange={setCategory}
        />

        {ordersError ? (
          <Typography className="mt-6 text-center text-[14px] text-[#DC2626]">
            {ordersError?.graphQLErrors?.[0]?.message ||
              ordersError?.message ||
              "Could not load orders."}
          </Typography>
        ) : null}

        <div className="relative mt-5 md:mt-8 md:overflow-x-auto">
          {ordersLoading && !mappedRows.length ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 py-10">
              <CircularProgress size={32} sx={{ color: "#2E1834" }} />
              <p className="text-[13px] text-[#6B7280]">Loading orders…</p>
            </div>
          ) : (
            <OrdersTable rows={pageRows} onViewDetails={handleView} />
          )}
        </div>

        <OrdersPagination
          page={safePage}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      </div>
    </div>
  );
}
